import React from 'react';
import { Box, Paper, Typography, Button, Alert, Grid, CircularProgress } from '@mui/material';
import RecurringRiskHeatmapCard from './RecurringRiskHeatmapCard';
import { AuditScopeBadge } from './GroupedIncidentReview';
import {
  PATTERN_CONTEXT_FOLLOWUP_SUBTITLE,
  PATTERN_CONTEXT_FOLLOWUP_TITLE,
} from '../../domain/incidents/groupedRepresentativeAuditCopy';
import { formatWorkflowStateLabel } from '../../utils/incidentStateLabels';
import type { IncidentDrawerDetailScope } from './incidentDrawerDetailTypes';

export function IncidentDrawerDetailStep4({ scope }: { scope: IncidentDrawerDetailScope }) {
  const s = scope as any;
  const zoneDisplay = (() => {
    const raw = String(s.selectedZoneName || s.selectedIncident.zone_id || '').trim();
    if (!raw) return 'N/A';
    return /demo5tes/i.test(raw) ? 'Restricted Forklift Zone' : raw;
  })();
  return (
    <Box ref={s.drawerStep4Ref} sx={s.stepSectionSx('step4')}>
      <Paper variant="outlined" sx={s.drawerCardSx}>
      {!s.groupedReviewMode ? (
        <Typography sx={s.cardLabelSx}>{s.journeyPhaseTitleForStep('step4')} phase</Typography>
      ) : null}
      {s.renderAuditStepHeading('step4', 'Step 4: Verify current condition')}
      {!s.groupedReviewMode ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.35, lineHeight: 1.55 }}>
          Check whether the condition is still active, who owns the next action, and when the team will review whether the situation improved.
        </Typography>
      ) : null}
      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Review status</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatWorkflowStateLabel(s.selectedIncident) || s.workflowLabel || 'Needs review'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Follow-up status</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.opsStateLabel || 'Not started'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Edge live condition</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.edgeLiveSummary}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Zone</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{zoneDisplay}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Camera stream</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.selectedCameraName || s.selectedIncident.camera_id || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Edge device</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.selectedIncident.device_id || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Opened</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {s.selectedIncident.created_at ? new Date(s.selectedIncident.created_at).toLocaleString() : '—'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Last updated</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {s.selectedIncident.updated_at ? new Date(s.selectedIncident.updated_at).toLocaleString() : '—'}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <Typography variant="caption" color="text.secondary">Target scope</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.targetSummary || '—'}</Typography>
        </Grid>
        {s.degradationSummary ? (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">System note</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.degradationSummary}</Typography>
          </Grid>
        ) : null}
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, fontWeight: 700 }}>
        Need more detail?
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {s.selectedIncident?.device_id && (
          <Button size="small" variant="text" onClick={s.openDiagnostics}>
            Open diagnostics
          </Button>
        )}
        {s.canOpenAuditTrail && (
          <Button size="small" variant="text" onClick={s.openAuditTrail}>
            Open audit trail
          </Button>
        )}
      </Box>
      </Paper>
      <Paper variant="outlined" sx={s.drawerCardSx}>
        {!s.groupedReviewMode ? (
        <Typography sx={s.cardLabelSx}>{s.journeyPhaseTitleForStep('step4')} phase</Typography>
      ) : null}
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 0.5 }}>
          <Typography component="span" sx={s.cardHeadingSx}>
            {s.groupedReviewMode ? PATTERN_CONTEXT_FOLLOWUP_TITLE : 'Current hotspot and action review'}
          </Typography>
          {s.groupedReviewMode ? <AuditScopeBadge label="Risk pattern" /> : null}
        </Box>
        {s.groupedReviewMode ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.1, lineHeight: 1.55 }}>
            {PATTERN_CONTEXT_FOLLOWUP_SUBTITLE}
          </Typography>
        ) : null}
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">Current hotspot level</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {s.recurringIssueSummary !== 'No longer-term recurrence trend reported'
                ? s.recurringIssueSummary
                : s.relatedIncidentCount > 1
                  ? 'Elevated in the current review window'
                  : 'Isolated right now'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">Current review window</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.relatedEventSignalsSummary}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="caption" color="text.secondary">Next review time</Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{s.nextReviewSummary}</Typography>
          </Grid>
        </Grid>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.35, lineHeight: 1.55 }}>
          {s.nextReviewRelativeSummary} Success means this same zone/camera combination cools off in the next review window instead of continuing to trend upward.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.2 }}>
          <Button size="small" variant="text" onClick={s.openRiskAnalytics}>
            Open current hotspot view
          </Button>
          {s.relatedIncidentCount > 1 ? (
            <Button size="small" variant="text" onClick={s.openRelatedIncidents}>
              Review pattern observations
            </Button>
          ) : null}
        </Box>
        <Box sx={{ mt: 1.5 }}>
          {s.heatmapLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : s.heatmapError ? (
            <Alert severity="warning">{s.heatmapError}</Alert>
          ) : (
            <RecurringRiskHeatmapCard
              data={s.heatmapData}
              dimension="zone"
              incidentType={s.safeText(s.selectedIncident?.incident_type)}
              incidentTypeOptions={
                s.safeText(s.selectedIncident?.incident_type)
                  ? [{ value: s.safeText(s.selectedIncident?.incident_type), label: s.currentTitle }]
                  : []
              }
              zoneNameById={s.zoneNameById}
              cameraNameById={s.cameraNameById}
              onDimensionChange={() => {}}
              onIncidentTypeChange={() => {}}
              onCellSelect={s.handleHeatmapCellSelect}
              openingOccurrence={s.heatmapOpening}
              cellSelectCaption={
                s.groupedReviewMode && s.groupedHeatmapCellSelectCaption
                  ? String(s.groupedHeatmapCellSelectCaption)
                  : undefined
              }
            />
          )}
        </Box>
      </Paper>
      {!s.groupedReviewMode ? s.renderStepNavigation('step4') : null}
    </Box>
  );
}
